import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useClerk } from '@clerk/react';
import { Button, Card, H3, H4, Body, BodySm } from '@/components/ui';
import { NotLegalIcon } from '@/components/ui/NotLegalIcon';
import { api } from '@/lib/api';
import type { ContractReview } from '@/types';

type Step = 'upload' | 'reviewing' | 'result';

export function ReviewPage() {
  const { session } = useClerk();
  const [step, setStep] = useState<Step>('upload');
  const [review, setReview] = useState<ContractReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const RISK_COLORS: Record<ContractReview['overall_risk'], string> = {
    low: 'text-green-700 bg-green-50 border-green-200',
    medium: 'text-amber-700 bg-amber-50 border-amber-200',
    high: 'text-red-700 bg-red-50 border-red-200',
  };

  const SECTION_COLORS = { green: 'border-green-300 bg-green-50', yellow: 'border-amber-300 bg-amber-50', red: 'border-red-300 bg-red-50' };

  const handleFile = useCallback(async (file: File) => {
    if (!file) return;
    setStep('reviewing');
    setError(null);

    try {
      if (session) {
        const token = await session.getToken();
        api.setAuthToken(token);
      }

      const text = await file.text();
      const result = await api.reviewContract(file.name, text);
      setReview(result);
      setStep('result');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to review contract');
      setStep('upload');
    }
  }, [session]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">
      <header className="bg-white border-b border-surface-200 px-6 py-4 flex items-center gap-3">
        <Link to="/chat">
          <Button variant="ghost" size="sm" icon={<NotLegalIcon name="arrowLeft" className="w-4 h-4" />}>
            Back to Chat
          </Button>
        </Link>
        <div className="h-5 w-px bg-surface-200" />
        <H3>Contract Review</H3>
      </header>

      <div className="flex-1 p-6 max-w-3xl mx-auto w-full">
        {step === 'upload' && (
          <div className="space-y-4">
            <Body className="text-surface-600">
              Upload a contract or agreement for an AI-powered risk analysis. Plain-text or .txt files work best.
            </Body>
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
            )}
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              className={`border-2 border-dashed rounded-2xl p-12 text-center transition-colors ${
                dragging ? 'border-primary-400 bg-primary-50' : 'border-surface-300 bg-white hover:border-surface-400'
              }`}
            >
              <NotLegalIcon name="document" className="w-12 h-12 text-surface-300 mx-auto mb-4" />
              <H4 className="mb-2">Drop your contract here</H4>
              <Body className="text-surface-500 mb-4">or click to browse</Body>
              <label className="cursor-pointer">
                <input type="file" accept=".txt,.md,text/*" className="sr-only" onChange={handleInputChange} />
                <Button variant="secondary" size="sm" as="span">Browse File</Button>
              </label>
            </div>
          </div>
        )}

        {step === 'reviewing' && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-3xl flex items-center justify-center mx-auto mb-6 animate-pulse">
              <NotLegalIcon name="document" className="w-8 h-8 text-primary-600" />
            </div>
            <H3 className="mb-2">Reviewing your contract...</H3>
            <Body className="text-surface-500">This usually takes 15-30 seconds.</Body>
          </div>
        )}

        {step === 'result' && review && (
          <div className="space-y-4">
            <Card padding="md" className={`border ${RISK_COLORS[review.overall_risk]}`}>
              <div className="flex items-center justify-between mb-2">
                <H4>Overall Risk: {review.overall_risk.toUpperCase()}</H4>
                <BodySm className="text-surface-400">{review.token_cost} tokens used</BodySm>
              </div>
              <Body>{review.summary}</Body>
            </Card>

            <div className="space-y-3">
              {review.sections.map((section, i) => (
                <Card key={i} padding="md" className={`border ${SECTION_COLORS[section.risk_level]}`}>
                  <H4 className="mb-1">{section.title}</H4>
                  <Body className="text-surface-700 mb-2">{section.summary}</Body>
                  {section.concern && (
                    <BodySm className="text-red-700"><strong>Concern:</strong> {section.concern}</BodySm>
                  )}
                  {section.suggestion && (
                    <BodySm className="text-green-700 mt-1"><strong>Suggestion:</strong> {section.suggestion}</BodySm>
                  )}
                </Card>
              ))}
            </div>

            <div className="flex justify-center pt-4">
              <Button variant="secondary" onClick={() => { setStep('upload'); setReview(null); }}>
                Review Another Contract
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
